import {
  type MutableRef,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'preact/hooks'
import { useLocation } from 'wouter-preact'
import { useTranslation } from 'react-i18next'
import { resetProcess } from '@/services/navigation'
import {
  getAppDetectionState,
  getProcessId,
  saveAppDetectionState,
} from '@/services/session'
import { createContext } from 'preact'
import { getConfig } from '../../../config'

interface ProtectedRouteProps {
  children: preact.ComponentChildren
}

interface ProtectionRouteContextType {
  isReloadProtectionActive: MutableRef<boolean>
}

const ProtectionRouteContext = createContext<
  ProtectionRouteContextType | undefined
>(undefined)

export const useProtectionRoute = () => {
  const context = useContext(ProtectionRouteContext)
  if (!context) {
    throw new Error(
      'useProtectionRoute must be used within ProtectionRouteProvider'
    )
  }
  return context
}

/**
 * Komponent chroniący routes przed bezpośrednim dostępem
 * Przy przeładowaniu strony lub bezpośrednim wpisaniu URL przekierowuje na start (/)
 * Do kolejnych stron można przejść tylko przez akcje użytkownika i nawigację
 */
export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { t } = useTranslation()
  const [location, setLocation] = useLocation()
  const [isPageReload, setIsPageReload] = useState<boolean | null>(null)
  const isReloadProtectionActive = useRef(true)

  useEffect(() => {
    const config = getConfig()
    const reloadProtectionEnabled = config.reloadProtectionEnabled ?? true

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      const processId = getProcessId()
      if (processId && isReloadProtectionActive.current) {
        // eslint-disable-next-line no-console
        console.log(
          'ProtectedRoute: Wykryto próbę opuszczenia strony z aktywnym procesem'
        )

        e.preventDefault()
        return 'Potwierdź, że chcesz opuścić stronę'
      }
      return undefined
    }
    if (location !== '/' && reloadProtectionEnabled) {
      window.addEventListener('beforeunload', handleBeforeUnload)
      // eslint-disable-next-line no-console
      console.log(
        'ProtectedRoute: Dodano handler beforeunload dla ochrony procesu'
      )
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [location])

  useEffect(() => {
    const config = getConfig()
    const reloadProtectionEnabled = config.reloadProtectionEnabled ?? true

    const handleUnload = () => {
      const processId = getProcessId()
      if (processId) {
        saveAppDetectionState({
          isRefreshedWithProcessId: true,
        })
        return
      }

      saveAppDetectionState({
        isRefreshedWithProcessId: false,
      })
    }

    if (reloadProtectionEnabled) {
      window.addEventListener('unload', handleUnload)
    }

    return () => {
      window.removeEventListener('unload', handleUnload)
    }
  }, [])

  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log(`ProtectedRoute: Sprawdzanie dostępu do ${location}`)
    const config = getConfig()
    const reloadProtectionEnabled = config.reloadProtectionEnabled ?? true

    const urlParams = new URLSearchParams(window.location.search)
    const returnToken = urlParams.get('returnToken')
    const { isRefreshedWithProcessId } = getAppDetectionState() ?? {}

    // Sprawdź czy to przeładowanie strony przez performance.navigation.type
    const isProhibitedReload =
      reloadProtectionEnabled &&
      (
        performance.getEntriesByType?.(
          'navigation'
        )?.[0] as PerformanceNavigationTiming
      )?.type === 'reload' &&
      isRefreshedWithProcessId

    const processId = getProcessId()

    if (location === '/summary' && returnToken) {
      sessionStorage.setItem('app_session_active', 'true')
      setIsPageReload(false)
      return
    }

    if (isProhibitedReload && location !== '/' && processId) {
      // eslint-disable-next-line no-console
      console.log(
        `ProtectedRoute: Wykryto przeładowanie/bezpośredni dostęp do ${location}, sprawdzanie processId`
      )
      // Użytkownik przeładował stronę pomimo ostrzeżenia - przekieruj na stronę błędu
      // eslint-disable-next-line no-console
      console.log(
        `ProtectedRoute: Wykryto processId ${processId}, przekierowanie na stronę błędu`
      )

      // Przekieruj na stronę błędu z kodem page-refresh
      window.history.replaceState(
        null,
        '',
        '/process-error?errorType=page-refresh'
      )
      setLocation('/process-error?errorType=page-refresh')
      return
    }

    if (
      !isProhibitedReload &&
      location !== '/' &&
      !sessionStorage.getItem('app_session_active')
    ) {
      // eslint-disable-next-line no-console
      console.log(
        `ProtectedRoute: Pierwsze wejście bez aktywnej sesji do ${location}, reset i przekierowanie na /`
      )

      // Resetuj stan procesu
      resetProcess()

      // Przekieruj na początek bez historii
      window.history.replaceState(null, '', '/')
      setLocation('/')
      return
    }

    if (
      !isProhibitedReload &&
      location === '/' &&
      sessionStorage.getItem('app_session_active') &&
      processId
    ) {
      // eslint-disable-next-line no-console
      console.log(
        `ProtectedRoute: Pierwsze wejście na / z aktywną sesją i processId ${processId}, przekierowanie do /`
      )

      // Resetuj stan procesu
      resetProcess()
    }

    // Oznacz sesję jako aktywną
    sessionStorage.setItem('app_session_active', 'true')

    // eslint-disable-next-line no-console
    console.log(
      `ProtectedRoute: Dostęp do ${location} dozwolony - nawigacja wewnętrzna`
    )
    setIsPageReload(false)
  }, [location, setLocation])

  // Loading state podczas sprawdzania
  if (isPageReload === null) {
    return (
      <div className='h-screen bg-white flex items-center justify-center'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-cencert-blue mx-auto mb-1'></div>
          <p className='text-gray-600'>{t('protectedRoute.checkingAccess')}</p>
        </div>
      </div>
    )
  }

  return (
    <ProtectionRouteContext.Provider value={{ isReloadProtectionActive }}>
      {children}
    </ProtectionRouteContext.Provider>
  )
}
